/**
 * Bengaluru Real Estate Micro-Markets Data Structure
 * 
 * This file defines the core geographical and commercial divisions of Bengaluru
 * for real estate market analysis and visualization. Each micro-market represents
 * a distinct area with similar characteristics in terms of:
 * - Commercial development patterns
 * - Residential demographics  
 * - Infrastructure connectivity
 * - Investment and growth trends
 * - Geographic proximity and accessibility
 * 
 * Data Structure:
 * - 5 micro-markets covering Bengaluru metropolitan area
 * - Complete postal code-based granular mapping for enhanced precision
 * - Postal code entries provide fine-grained boundary control
 * 
 * Usage:
 * - Color-coding for map visualization (each micro-market has distinct color)
 * - Feature Layer Place ID mapping for Google Maps integration
 * - Market analysis and locality grouping
 * - Real estate investment zone identification
 * 
 * Updated: July 2024 - Phase 1 Complete: All 5 Micro-Markets Implemented
 * Data Source: Feature Layer Place ID discovery via postal code mapping
 * Status: Production ready - validated with Google Maps Feature Layer integration
 */

export const microMarkets = {
  /**
   * Central Micro-Market (Red: #C62828)
   * Premium business district and commercial hub
   * Characteristics: High-end commercial, business centers, premium retail
   * Phase 1: Postal code-based granular coverage implemented
   */
  Central: [
    // Postal code areas for fine-grained coverage
    "Central_560051", "Central_560001", "Central_560009", "Central_560027", 
    "Central_560025", "Central_560004", "Central_560042", "Central_560002",
    "Central_560007", "Central_560047", "Central_560005", "Central_560046",
    "Central_560008", "Central_560011", "Central_560029"
  ],

  /**
   * East Micro-Market (Blue: #1565C0) 
   * IT corridor and technology hub
   * Characteristics: Tech companies, modern infrastructure, IT workforce residential
   * Phase 1: Postal code-based granular coverage implemented
   */
  East: [
    // Postal code areas for fine-grained coverage
    "East_560066", "East_560067", "East_560087", "East_560075",
    "East_560037", "East_560017", "East_560103", "East_560102",
    "East_560101", "East_563103", "East_560049", "East_560016",
    "East_560036", "East_560048"
  ],

  /**
   * West Micro-Market (Green: #2E7D32)
   * Established residential and cultural areas
   * Characteristics: Traditional residential, educational institutions, cultural centers
   * Phase 1: Postal code-based granular coverage implemented
   */
  West: [
    // Postal code areas for fine-grained coverage  
    "West_560022", "West_560012", "West_560055", "West_560094",
    "West_560010", "West_560024", "West_560003", "West_560080",
    "West_560032", "West_560006", "West_560096", "West_560021",
    "West_560079", "West_560054", "West_560058", "West_562127",
    "West_562132", "West_562123", "West_560091", "West_560104",
    "West_560072", "West_560023", "West_560026", "West_560040"
  ],

  /**
   * North Micro-Market (Orange: #FF8F00)
   * Emerging growth areas and connectivity hubs  
   * Characteristics: Rapid development, infrastructure expansion, connectivity projects
   * Phase 1: Postal code-based granular coverage implemented
   */
  North: [
    // Postal code areas for fine-grained coverage
    "North_560045", "North_560063", "North_562157", "North_560064",
    "North_560077", "North_560043", "North_560084", "North_560092",
    "North_560033", "North_560038"
  ],

  /**
   * South Micro-Market (Purple: #6A1B9A)
   * Mixed development and educational institutions
   * Characteristics: Educational hubs, mixed commercial-residential, growth corridors
   * Phase 1: Postal code-based granular coverage implemented
   */
  South: [
    // Postal code areas for fine-grained coverage
    "South_560030", "South_560095", "South_560034", "South_560071",
    "South_560068", "South_560035", "South_560019", "South_560041",
    "South_560050", "South_560070", "South_560078", "South_560076",
    "South_560102", "South_562109", "South_560082", "South_560083",
    "South_560105"
  ]
} as const; 